import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";

export default function AIMLServicesSection({ data }) {
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");

  // Use services from API data directly
  const services = data?.services || [];

  // API server URL from environment variable with fallback
  const API_SERVER_URL = process.env.REACT_APP_API_URL;
  
  // Helper function to construct image URL
  const getImageUrl = (imageData) => {
    if (!imageData || !imageData.url) return null;
    
    // If URL is already complete (starts with http), use as-is
    if (imageData.url.startsWith('http')) {
      return imageData.url;
    }
    
    // Otherwise, construct URL with base
    const baseUrl = API_SERVER_URL?.replace('/api', '') || 'http://localhost:1337';
    return `${baseUrl}${imageData.url}`;
  };

  return (
    <Box py={{ base: 12, md: 16 }}>
      <Container maxW="7xl" px={{ base: 4, md: 8 }}>
        {/* Header Section */}
        <VStack
          spacing={{ base: 4, md: 6 }}
          align="center"
          textAlign="center"
          mb={{ base: 8, md: 12 }}
        >
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            fontWeight="bold"
            color={headingColor}
            lineHeight="shorter"
          >
            {data?.serviceTitle?.title}
          </Heading>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={textColor}
            maxW="4xl"
            lineHeight="relaxed"
          >
            {data?.serviceTitle?.description}
          </Text>
        </VStack>

        {/* Services Grid */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={{ base: 6, md: 8 }}
          maxW="full"
        >
          {services.map((service, index) => (
            <Box
              key={index}
              p={{ base: 6, md: 8 }}
              borderRadius="lg"
              transition="all 0.3s ease"
              _hover={{
                shadow: "md",
                transform: "translateY(-2px)",
              }}
              h="full"
            >
              <VStack
                spacing={{ base: 4, md: 5 }}
                align="center"
                textAlign="center"
                h="full"
              >
                {/* Icon */}
                <Box
                  w="60px"
                  h="60px"
                  bg="blue.50"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  overflow="hidden"
                >
                  {service.image ? (
                    <Image 
                      src={getImageUrl(service.image)}
                      alt={service.title}
                      boxSize="35px"
                      objectFit="contain"
                      onLoad={() => console.log(`AIMLServicesSection image loaded: ${service.title}`)}
                      onError={(e) => console.log(`AIMLServicesSection image failed: ${service.title}`, e.target.src)}
                    />
                  ) : (
                    <Box w="35px" h="35px" bg="#000B8C" borderRadius="md" />
                  )}
                </Box>

                {/* Title */}
                <Heading
                  as="h3"
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="bold"
                  color={headingColor}
                  lineHeight="tight"
                >
                  {service.title}
                </Heading>

                {/* Description */}
                <Text
                  color={textColor}
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight="relaxed"
                  flex="1"
                >
                  {service.description}
                </Text>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
